import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [isMobile, setIsMobile] = useState(false);
  
  const { register, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const validateForm = () => {
    const newErrors: any = {};

    if (!firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) return;

    try {
      await register(email, password, firstName, lastName);
      navigate('/user/dashboard'); // Redirect to dashboard on success
    } catch (err) {
      // Error is handled in AuthContext
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account - Oracle Gaming</title>
        <meta name="description" content="Create an account and start your journey with Oracle Gaming. Earn rewards, track orders, and more." />
      </Helmet>
      <Header />
      <div className="h-20" />
      <section className="gallery-carousel" style={{ paddingTop: isMobile ? '220px' : '120px', paddingBottom: isMobile ? '40px' : '60px' }}>
        <div className="container">
          <h2 className="h2 section-title" style={{ color: 'var(--white)', marginBottom: isMobile ? '24px' : '40px' }}>CREATE AN ACCOUNT</h2>
          
          <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}>
            <div className="bg-primarySupport shadow-lg border border-black/20" style={{ padding: isMobile ? '30px 20px' : '40px 24px' }}>
              <p className="text-center mb-6" style={{ color: 'var(--platinum)' }}>
                Create an account and start your journey!
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500" style={{ color: '#f48771', marginBottom: '24px' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="firstName" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.firstName ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="John"
                  />
                  {errors.firstName && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.firstName}</p>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="lastName" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="Doe"
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="email" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.email ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.email}</p>}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="password" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.password ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="••••••••"
                  />
                  {errors.password && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.password}</p>}
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="confirmPassword" className="block" style={{ color: 'var(--white)', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--raisin-black-1)',
                      border: errors.confirmPassword ? '1px solid #f48771' : '1px solid rgba(255, 107, 0, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--white)',
                      fontSize: '16px',
                    }}
                    placeholder="••••••••"
                  />
                  {errors.confirmPassword && <p style={{ marginTop: '8px', fontSize: '14px', color: '#f48771' }}>{errors.confirmPassword}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    marginBottom: '20px',
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>

              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <p style={{ color: 'var(--platinum)', fontSize: '14px', marginBottom: '12px' }}>
                  Already have an account?{' '}
                  <Link to="/login" className="btn-link" style={{ color: 'var(--orange)' }}>
                    Login here
                  </Link>
                </p>
                <Link to="/shop" style={{ color: 'var(--platinum)', fontSize: '14px' }}>
                  Continue as guest
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

